import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { getThemeColor } from '../../utils/styles';
import Container from '../container';
import Title from '../title';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import { Grid, Paragraph } from '../UIKit/system';
import IconButton from '../icon-button';
import RichTextField from '../rich-text-field';
import Button from '../button';
import mq from '../../utils/mq';

interface CustomisationContent {
  _uid: string;
  name: string;
  content: {
    _uid: string;
    items: {
      _uid: string;
      title: string;
      text: Record<string, any>;
      modalImage?: Record<string, any>;
    }[];
  }[];
}

interface CustomisationModalProps {
  title?: string;
  content: CustomisationContent[];
  onClose: () => void;
}

export const CustomisationModal: FC<CustomisationModalProps> = (props) => {
  const { content, title, onClose } = props;
  const { startingTabId, tabs, tabsContent } = mapDataForModal(content);
  const [activeTab, setActiveTab] = useState(startingTabId);

  const onChangeTab = (tabId: string) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setActiveTab(tabId);
    };
  };

  return (
    <Modal>
      <Scroll>
        <MContainer>
          <Content>
            <Grid
              gridAutoFlow="column"
              alignItems="center"
              justifyContent="space-between"
              pb={40}
            >
              <Title fontSizeMedium>{title}</Title>
              <CloseButton onClick={onClose} />
            </Grid>
            <Grid gridAutoFlow={['row', 'row', 'column']} gridGap={38}>
              <TextGrid>
                <div>
                  {tabs.map((tab) => (
                    <div key={tab.id}>
                      <TextTitle>{tab.title}</TextTitle>
                      <TabButtonsGrid>
                        {tab.subTabs.map((subTab) => (
                          <Button
                            to="#"
                            key={subTab.id}
                            onClick={onChangeTab(subTab.id)}
                            primaryTransparent={activeTab === subTab.id}
                            greyTransparent={activeTab !== subTab.id}
                            justifyContent="start"
                            height={46}
                          >
                            {subTab.title}
                          </Button>
                        ))}
                      </TabButtonsGrid>
                    </div>
                  ))}
                </div>
                <div>
                  {tabsContent
                    .filter((tabContent) => tabContent.tabId === activeTab)
                    .map((tabContent) => (
                      <ContentItem key={tabContent.id}>
                        <div>
                          <TextTitle>{tabContent.title}</TextTitle>
                          <Paragraph color="grey" fontSizeSmall>
                            <RichTextField data={tabContent.text} />
                          </Paragraph>
                        </div>

                        <ImageContainer>
                          {tabContent.image && (
                            <LazyImage
                              fluid={getFluidGatsbyImage(tabContent.image)}
                            />
                          )}
                        </ImageContainer>
                      </ContentItem>
                    ))}
                </div>
              </TextGrid>
            </Grid>
          </Content>
        </MContainer>
      </Scroll>
    </Modal>
  );
};

const Modal = styled.div`
  background-color: rgb(from ${getThemeColor('black')} 0 0 0 / 75%);
  position: fixed;
  min-height: 100vh;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
`;

const Scroll = styled.div`
  height: 100vh;
  overflow-y: scroll;
`;

const MContainer = styled(Container).attrs(() => ({
  large: true,
  my: [50, 50, 100]
}))``;

const CloseButton = styled(IconButton).attrs(() => ({
  icon: { name: 'cross' }
}))`
  height: 16px;
  width: 16px;
`;

const Content = styled.div`
  background-color: ${getThemeColor('white')};
  padding: 48px;
`;

const TextGrid = styled(Grid).attrs(() => ({
  gridTemplateColumns: [undefined, '1fr 2.5fr']
}))`
  column-gap: 30px;
`;

const ContentItem = styled(Grid).attrs(() => ({
  gridGap: 30
}))`
  grid-template-columns: repeat(2, 1fr);

  & + & {
    margin-top: 30px;
  }

  ${mq.desktopSmall(css`
    grid-template-columns: none;
    grid-auto-flow: rows;
  `)}
`;

const TextTitle = styled(Paragraph).attrs(() => ({
  fontWeight: 700,
  fontSizeMedium: true,
  pb: 12
}))``;

const ImageContainer = styled(Grid).attrs(() => ({
  justifySelf: [undefined, 'center'],
  width: [undefined, 443]
}))`
  aspect-ratio: 2 / 1.5;

  &:empty {
    height: 0px;
  }
`;

const TabButtonsGrid = styled(Grid).attrs(() => ({
  justifyItems: 'start',
  mb: [24, 40]
}))``;

const mapDataForModal = (content: CustomisationModalProps['content']) => {
  const tabs = content.map((tab) => {
    return {
      id: tab._uid,
      title: tab.name,
      subTabs: tab.content.map((subTab) => {
        const firstItem = subTab.items[0];

        return {
          id: subTab._uid,
          title: firstItem.title
        };
      })
    };
  });
  const tabsContent: Array<{
    id: string;
    tabId: string;
    title: string;
    text: Record<string, any>;
    image?: Record<string, any>;
  }> = [];

  for (const tab of content) {
    for (const subTab of tab.content) {
      for (const item of subTab.items) {
        tabsContent.push({
          id: item._uid,
          tabId: subTab._uid,
          title: item.title,
          text: item.text,
          image: item.modalImage
        });
      }
    }
  }

  const startingTabId = tabs[0].subTabs[0].id;

  return { startingTabId, tabs, tabsContent };
};
